import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import {
  Button,
  Divider,
  InpockInput,
  InpockView,
  Paragraph1,
  Paragraph2,
  Rules,
} from 'inpock-ui-react';
import queryString from 'query-string';
import styled from '@emotion/styled';
import { colors } from 'inpock-ui-react/dist/src/color';

import { openPopup, ORDER_TEMPLATE } from '@/utils';
import {
  Dialog,
  Icon,
  PostcodeDialog,
  PostcodePage,
  PageTitle,
  OrderInfoSummary,
  OrderInfoSection,
  Callout,
  DashDivider,
  OrderInfoLine,
  ReviewSheet,
} from '@/components';
import {
  useOrderGroupDetailQuery,
  useOrder,
  useOrderShipping,
  useReviewableOrderlinesQuery,
  useDialog,
} from '@/hooks';
import { OrderStatus, OrderlineStatus, OrdersDetailResponse, api } from '@/apis';
import {
  CUSTOMER_ADDRESS,
  CUSTOMER_ADDRESS_DETAIL,
  CUSTOMER_NAME,
  CUSTOMER_PHONE,
  CUSTOMER_ZIPCODE,
} from '@/constants';

export function OrderDetailPage({ isMember }: { isMember: boolean }) {
  const params = useParams();
  const navigate = useNavigate();
  const [dialogOn] = useDialog();

  const [backdropElement, setBackdropElement] = useState<HTMLDivElement | null>(null);
  const [isShowingPostcode, setIsShowingPostcode] = useState<boolean>(false);
  const { REACT_APP_LINK_CLIENT_URL } = process.env;

  const { id } = params;
  const query = queryString.parse(useLocation().search);
  const { phone, another_review } = query as unknown as {
    phone: number;
    customer: string;
    another_review: string;
  };

  const orderQueryParams = {
    orderGroupNumber: id as string,
    loggedIn: isMember,
    ...(isMember ? {} : { phone, options: { onError: () => navigate('/anonymous') } }),
  };

  const { data: order } = useOrderGroupDetailQuery(orderQueryParams);

  // another_review가 ture일 때 리뷰 작성 가능한 주문 상품 조회
  const { data: reviewableOrderlines } = useReviewableOrderlinesQuery(
    id as string,
    isMember && another_review === 'true',
  );
  const showReviewSheet =
    isMember &&
    another_review === 'true' &&
    reviewableOrderlines &&
    reviewableOrderlines?.length > 0;

  const { showEditDeliveryBtn } = useOrder(order as OrdersDetailResponse);
  const {
    shippingInfo,
    setShippingInfo,
    onInputShippingType,
    showDeliveryModal,
    setShowDeliveryModal,
    onClickEditShippingModal,
  } = useOrderShipping(order);

  const orderlines = order?.orders?.flatMap((order) => order.orderlines);

  const onRouteHome = () => (window.location.href = `${REACT_APP_LINK_CLIENT_URL}/customer/menu`);

  // 회원만 있는 기능
  const onClickCustomerOrders = () => {
    navigate('/customer/orders', { replace: true });
  };

  const handlePostcodeComplete = async (result: { postcode: string; address: string }) => {
    const { postcode, address } = result;
    const { shipping_type } = await api.orders.getShippingType(postcode);
    dialogOn({
      template: ORDER_TEMPLATE.shippingType.change(),
      close: {
        text: '확인',
      },
    });

    await setShippingInfo((prev) => ({
      ...prev,
      shipping_type,
      receiver_postcode: postcode,
      receiver_address: address,
      receiver_detail_address: '',
    }));

    setIsShowingPostcode(false);
  };

  const onClickCashReceipt = () => {
    if (order?.cash_receipt_url) {
      window.open(order.cash_receipt_url, '_blank');
      return;
    }
    const path = isMember
      ? `/customer/orders/${id}/cash-receipt`
      : `/anonymous/orders/${id}/cash-receipt?phone=${phone}`;

    navigate(path, {
      state: { order: order },
    });
  };

  useEffect(() => {
    setBackdropElement(document.getElementById('backdrop') as HTMLDivElement);
  }, []);

  useEffect(() => {
    if (another_review && reviewableOrderlines && reviewableOrderlines.length === 0) {
      navigate(`/customer/orders/${id}`, { replace: true });
    }
  }, [another_review, reviewableOrderlines]);

  return (
    <>
      {showReviewSheet && (
        <ReviewSheet
          visible={showReviewSheet}
          orderGroupNumber={id as string}
          orderlines={reviewableOrderlines}
          onDismiss={() => {
            navigate(`/customer/orders/${id}`, { replace: true });
          }}
        />
      )}
      {showDeliveryModal && (
        <Dialog onHide={() => setShowDeliveryModal(false)} preventHide={true}>
          <div style={{ padding: '26px 16px' }}>
            <Paragraph1 style={{ textAlign: 'left', marginBottom: '20px' }}>
              배송 정보 수정
            </Paragraph1>
            <InpockInput
              style={{ width: '100%' }}
              value={shippingInfo.receiver_name}
              onInput={(e) => setShippingInfo({ ...shippingInfo, receiver_name: e })}
              guide="받는 사람"
              placeholder="홍길동"
              required
              clearable
              maxLength={CUSTOMER_NAME.rules?.max_length}
              validator={(_rules, value) =>
                CUSTOMER_NAME.validator &&
                CUSTOMER_NAME.validator(CUSTOMER_NAME.rules as Rules, value)
              }
              rules={CUSTOMER_NAME.rules}
            />
            <InpockInput
              style={{ width: '100%' }}
              value={shippingInfo.receiver_phone}
              onInput={(e) => setShippingInfo({ ...shippingInfo, receiver_phone: e })}
              type="text"
              inputMode="numeric"
              guide="연락처"
              placeholder="01000000000"
              required
              clearable
              maxLength={CUSTOMER_PHONE.rules?.max_length}
              validator={(_rules, value) =>
                CUSTOMER_PHONE.validator &&
                CUSTOMER_PHONE.validator(CUSTOMER_PHONE.rules as Rules, value)
              }
              rules={CUSTOMER_PHONE.rules}
            />
            <InpockInput
              style={{ width: '100%', marginBottom: '12px' }}
              value={shippingInfo.receiver_postcode}
              onInput={() => {}}
              guide="주소"
              placeholder="우편번호"
              clearable
              disabled
              type="text"
              maxLength={CUSTOMER_ZIPCODE.rules?.max_length}
              validator={(_rules, value) =>
                CUSTOMER_ZIPCODE.validator &&
                CUSTOMER_ZIPCODE.validator(CUSTOMER_ZIPCODE.rules as Rules, value)
              }
              rules={CUSTOMER_ZIPCODE.rules}
              postfix={
                <Button size="medium" type="secondary" onClick={() => setIsShowingPostcode(true)}>
                  우편번호 검색
                </Button>
              }
            />
            <InpockInput
              style={{ width: '100%', marginBottom: '12px' }}
              value={shippingInfo.receiver_address}
              onInput={() => {}}
              type="text"
              placeholder="배송지"
              clearable
              disabled
              maxLength={CUSTOMER_ADDRESS.rules?.max_length}
              validator={(_rules, value) =>
                CUSTOMER_ADDRESS.validator &&
                CUSTOMER_ADDRESS.validator(CUSTOMER_ADDRESS.rules as Rules, value)
              }
              rules={CUSTOMER_ADDRESS.rules}
            />
            <InpockInput
              style={{ width: '100%', marginBottom: '16px' }}
              value={shippingInfo.receiver_detail_address}
              onInput={(e) => setShippingInfo({ ...shippingInfo, receiver_detail_address: e })}
              type="text"
              placeholder="상세주소"
              clearable
              maxLength={CUSTOMER_ADDRESS_DETAIL.rules?.max_length}
              validator={(_rules, value) =>
                CUSTOMER_ADDRESS_DETAIL.validator &&
                CUSTOMER_ADDRESS_DETAIL.validator(CUSTOMER_ADDRESS_DETAIL.rules as Rules, value)
              }
              rules={CUSTOMER_ADDRESS_DETAIL.rules}
            />

            <div className="button-container d-flex" style={{ gap: '10px' }}>
              <Button
                type="default"
                size="medium"
                onClick={() => setShowDeliveryModal(false)}
                style={{ flex: 3 }}
                fullWidth
              >
                닫기
              </Button>
              <Button
                type="primary"
                size="medium"
                onClick={() => onClickEditShippingModal(isMember, id as string, phone)}
                style={{ flex: 7 }}
                disabled={
                  shippingInfo.receiver_address === '' ||
                  shippingInfo.receiver_detail_address === '' ||
                  shippingInfo.receiver_name === '' ||
                  shippingInfo.receiver_phone === '' ||
                  shippingInfo.receiver_postcode === ''
                }
                fullWidth
              >
                수정하기
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      {backdropElement &&
        isShowingPostcode &&
        createPortal(
          <PostcodeDialog onHide={setIsShowingPostcode}>
            <PostcodePage onComplete={handlePostcodeComplete} />
          </PostcodeDialog>,
          backdropElement,
        )}

      <HelmetProvider>
        <Helmet>
          <title>주문상세내역 | 인포크링크</title>
        </Helmet>
      </HelmetProvider>

      <InpockView>
        <div className="container">
          {isMember ? (
            <PageTitle
              title="주문상세내역"
              onClick={onClickCustomerOrders}
              rightSideNode={<Icon icon="Home" size={16} color="#343434" onClick={onRouteHome} />}
            />
          ) : (
            <PageTitle
              title="주문상세내역"
              rightSideNode={<Icon icon="Home" size={16} color="#343434" onClick={onRouteHome} />}
              noIcon
            />
          )}
          <OrderInfoSummary order={order as OrdersDetailResponse} />

          <OrderInfoSection order={order} type="campaign" isMember={isMember} phone={phone}>
            <Callout
              text="✋ 업데이트된 배송상태와 실제 배송현황은 다를 수 있어요. 정확한 정보는 송장 번호를 조회하여 확인할 수 있으며 자세한 사항은 판매자에게 문의해주세요."
              className="mb-20"
            />
          </OrderInfoSection>
          <Divider wide />
          {/* 결제 정보 */}
          <OrderInfoSection
            order={order}
            type="payment"
            actionTitle={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {order?.receipt_url && (
                  <PayInfoButton onClick={() => openPopup(order.receipt_url as string)}>
                    <Paragraph2>결제내역서</Paragraph2>
                  </PayInfoButton>
                )}
                {order?.cash_receipt_info || order?.cash_receipt_url ? (
                  <PayInfoButton onClick={onClickCashReceipt}>
                    <Paragraph2>현금영수증</Paragraph2>
                  </PayInfoButton>
                ) : null}
              </div>
            }
          >
            <>
              {orderlines &&
              orderlines.some((orderline) => orderline?.status === OrderlineStatus.CANCELED) ? (
                <>
                  <DashDivider />
                  <div style={{ marginTop: '12px' }}>
                    <OrderInfoLine
                      label="최초 결제금액"
                      content={`${order?.initial_total_price.toLocaleString()}원`}
                    />
                    <OrderInfoLine
                      label="환불금액"
                      content={`${order?.cancel_price.toLocaleString()}원`}
                    />
                  </div>
                </>
              ) : null}
              <div className="total-price" style={{ marginTop: '12px' }}>
                <OrderInfoLine
                  label={
                    order?.orders.some((order) => order.status === OrderStatus.UNPAID)
                      ? '총 주문 금액'
                      : '총 결제 금액'
                  }
                  content={`${(order?.total_price ?? '').toLocaleString()}원`}
                  valueColor="orange"
                />
              </div>
            </>
          </OrderInfoSection>
          <Divider wide />
          {/* 배송 정보 */}
          <OrderInfoSection order={order} type="delivery">
            {showEditDeliveryBtn && (
              <div style={{ width: '100%', marginTop: '14px' }}>
                <Button
                  fullWidth
                  type="line"
                  size="medium"
                  onClick={() => setShowDeliveryModal(true)}
                >
                  배송지 수정
                </Button>
              </div>
            )}
          </OrderInfoSection>

          <Divider wide />
          {/* 구매자 정보 */}
          <OrderInfoSection order={order} type="buyer" />
        </div>
      </InpockView>
    </>
  );
}

const ShippingTypeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 40px;

  .shipping-type-list {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .shipping-type {
    position: relative;
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: 6px;
  }
`;

const PayInfoButton = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 8px 2px 8px;
  outline: none;
  border-radius: 4px;
  border: 1px solid ${colors.gray200};
  background-color: ${colors.gray100};

  color: ${colors.gray800};

  :hover {
    background-color: ${colors.white};
  }

  :active {
    background-color: ${colors.gray200};
  }
`;

const SectionBody = styled.div`
  padding: 8px 12px 0;
  display: flex;
  flex-direction: column;
`;
